// use-filmstrip-drag.ts
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DIRECTION_LOCK_PX,
  SETTLE_MS,
  decideCommit,
  easeOutCubic,
  estimateVelocity,
  rubberBand,
  type DragSample,
} from "./filmstrip-physics";

interface UseFilmstripDragOptions {
  count: number;
  /** When true the zoom engine owns the gesture; the carousel ignores drags. */
  zoomed: boolean;
  initialIndex: number;
  /** The stage element, measured at drag start to convert px → slide fractions. */
  stageRef: React.RefObject<HTMLElement | null>;
}

export interface FilmstripDrag {
  /** The committed photo index (advances only when a drag/step settles). */
  index: number;
  /** Track position in slide units; resting value is `-index`. */
  trackFraction: number;
  dragging: boolean;
  animating: boolean;
  canPrev: boolean;
  canNext: boolean;
  /** Programmatic one-step move (chevron / keyboard); no wrap. */
  step: (dir: 1 | -1) => void;
  /**
   * Jump straight to any photo (a thumbnail tap), with no tween. Only the
   * current slide and its neighbours are mounted, so easing across several
   * slides would sweep through empty frames. Out-of-range indices are ignored.
   */
  goTo: (target: number) => void;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

export function useFilmstripDrag({
  count,
  zoomed,
  initialIndex,
  stageRef,
}: UseFilmstripDragOptions): FilmstripDrag {
  const [index, setIndex] = useState(initialIndex);
  const [trackFraction, setTrackFraction] = useState(-initialIndex);
  const [dragging, setDragging] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Mirror trackFraction in a ref so the rAF tween and pointer handlers read the
  // live value without re-subscribing.
  const fracRef = useRef(-initialIndex);
  const setFrac = useCallback((v: number) => {
    fracRef.current = v;
    setTrackFraction(v);
  }, []);

  const indexRef = useRef(index);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const drag = useRef<{
    startX: number;
    startY: number;
    width: number;
    lock: null | "h" | "v";
    samples: DragSample[];
  } | null>(null);

  // Active pointer ids on the stage. >1 means a pinch — the carousel yields to zoom.
  const pointers = useRef<Set<number>>(new Set());

  const targetRef = useRef(initialIndex);
  const rafId = useRef<number | null>(null);
  const cancelAnim = useCallback(() => {
    if (rafId.current != null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
    setAnimating(false);
  }, []);

  // Finish any in-flight tween at its destination so a new gesture starts from a
  // resting slide (no stuck mid-positions) and rapid swipes chain cleanly.
  const finishAnim = useCallback(() => {
    if (rafId.current == null) return;
    cancelAnimationFrame(rafId.current);
    rafId.current = null;
    const target = targetRef.current;
    indexRef.current = target;
    setIndex(target);
    setFrac(-target);
    setAnimating(false);
  }, [setFrac]);

  // Eased rAF tween of trackFraction to a target index, settling `index` at the end.
  const animateTo = useCallback(
    (target: number) => {
      targetRef.current = target;
      cancelAnim();
      const from = fracRef.current;
      const to = -target;
      if (from === to) {
        setIndex(target);
        return;
      }
      setAnimating(true);
      let startTs: number | null = null;
      const tick = (ts: number) => {
        if (startTs == null) startTs = ts;
        const t = Math.min((ts - startTs) / SETTLE_MS, 1);
        setFrac(from + (to - from) * easeOutCubic(t));
        if (t < 1) {
          rafId.current = requestAnimationFrame(tick);
        } else {
          rafId.current = null;
          setFrac(to);
          setIndex(target);
          setAnimating(false);
        }
      };
      rafId.current = requestAnimationFrame(tick);
    },
    [cancelAnim, setFrac],
  );

  const step = useCallback(
    (dir: 1 | -1) => {
      if (zoomed) return;
      const target = indexRef.current + dir;
      if (target < 0 || target > count - 1) return; // no wrap
      animateTo(target);
    },
    [zoomed, count, animateTo],
  );

  const goTo = useCallback(
    (target: number) => {
      if (target < 0 || target > count - 1) return;
      cancelAnim();
      drag.current = null;
      setDragging(false);
      targetRef.current = target;
      indexRef.current = target;
      setIndex(target);
      setFrac(-target);
    },
    [count, cancelAnim, setFrac],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (zoomed || count < 2) return;
      pointers.current.add(e.pointerId);
      // A second pointer means a pinch is starting (still at scale 1): abandon any
      // carousel drag so a pinch finger's motion can't be read as a swipe.
      if (pointers.current.size > 1) {
        drag.current = null;
        setDragging(false);
        return;
      }
      finishAnim(); // settle any in-flight tween before a new gesture
      const width = stageRef.current?.getBoundingClientRect().width ?? 0;
      drag.current = {
        startX: e.clientX,
        startY: e.clientY,
        width,
        lock: null,
        samples: [{ x: e.clientX, t: e.timeStamp }],
      };
    },
    [zoomed, count, finishAnim, stageRef],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (pointers.current.size > 1) return; // multi-touch (pinch) — not a swipe
      const d = drag.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (d.lock == null) {
        if (Math.abs(dx) < DIRECTION_LOCK_PX && Math.abs(dy) < DIRECTION_LOCK_PX)
          return;
        d.lock = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
        if (d.lock === "h") {
          try {
            (e.target as Element).setPointerCapture(e.pointerId);
          } catch {
            // jsdom / unsupported — capture is a nicety, not required.
          }
          setDragging(true);
        }
      }
      if (d.lock !== "h" || d.width <= 0) return;
      d.samples.push({ x: e.clientX, t: e.timeStamp });
      const eff = rubberBand(dx, indexRef.current, count);
      setFrac(-indexRef.current + eff / d.width);
    },
    [count, setFrac],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      pointers.current.delete(e.pointerId);
      const d = drag.current;
      drag.current = null;
      if (!d || d.lock !== "h") {
        setDragging(false);
        return;
      }
      setDragging(false);
      const dx = e.clientX - d.startX;
      const velocity = estimateVelocity(d.samples);
      const target = decideCommit({
        dx,
        velocity,
        stageWidth: d.width,
        index: indexRef.current,
        count,
      });
      animateTo(target);
    },
    [count, animateTo],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent) => {
      pointers.current.delete(e.pointerId);
      const d = drag.current;
      drag.current = null;
      setDragging(false);
      // A cancelled gesture must never commit — snap back to the current photo.
      if (d && d.lock === "h") animateTo(indexRef.current);
    },
    [animateTo],
  );

  // Clamp if the photo set shrinks under us. React permits calling setState during
  // render to adjust derived state (docs.react.dev/reference/react/useState §
  // "Storing information from previous renders"). We call only pure state setters here
  // — no ref mutations — so neither react-hooks/refs nor react-hooks/set-state-in-effect
  // fire. fracRef stays in sync because setFrac (used everywhere else) writes both
  // state and ref atomically; the clamp path only needs the state.
  if (index > count - 1) {
    const clamped = Math.max(count - 1, 0);
    setIndex(clamped);
    setTrackFraction(-clamped);
  }

  // Cancel any in-flight tween on unmount.
  useEffect(() => cancelAnim, [cancelAnim]);

  return {
    index,
    trackFraction,
    dragging,
    animating,
    canPrev: index > 0,
    canNext: index < count - 1,
    step,
    goTo,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  };
}
